import React, {useEffect, useRef, useState} from 'react';
import DatePicker from 'react-datepicker';
import InputMask from 'react-input-mask';
import 'react-datepicker/dist/react-datepicker.css';

interface MaskedDatePickerProps {
    selected?: Date | null;
    onChange: (date?: Date) => void;
    placeholderText?: string;
    locale?: string;
    dateFormat?: string;
    minDate?: Date | null;
    maxDate?: Date | null;
    onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
    readOnly?: boolean;
    disabled?: boolean;
}

const MaskedDatePicker: React.FC<MaskedDatePickerProps> = ({
                                                               selected,
                                                               onChange,
                                                               placeholderText = "ДД.ММ.ГГГГ",
                                                               locale = "ru",
                                                               dateFormat = "dd.MM.yyyy",
                                                               minDate = null,
                                                               maxDate = null,
                                                               onKeyDown,
                                                               readOnly = false,
                                                               disabled = false,
                                                           }) => {
    const formatValue = (date: Date | null | undefined): string => {
        if (date instanceof Date && !isNaN(date.getTime())) {
            return date.toLocaleDateString('ru-RU');
        }
        return '';
    };

    const [inputValue, setInputValue] = useState<string>(formatValue(selected));
    const [internalDate, setInternalDate] = useState<Date | null>(
        selected instanceof Date && !isNaN(selected.getTime()) ? selected : null
    );
    const userInitiated = useRef(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const selectionRef = useRef<{ start: number | null; end: number | null }>({ start: null, end: null });

    useEffect(() => {
        if (userInitiated.current) {
            userInitiated.current = false;
            return;
        }
        const newDate = selected instanceof Date && !isNaN(selected.getTime()) ? selected : null;
        setInternalDate(newDate);
        setInputValue(formatValue(newDate));
    }, [selected]);

    const hasDigits = (value: string): boolean => /\d/.test(value);

    const isEmptyMask = (value: string): boolean => {
        return !value || value === '__.__.____' || !hasDigits(value);
    };

    const parseDate = (value: string): Date | null => {
        if (!value || value.includes('_')) return null;
        const parts = value.split(".");
        if (parts.length === 3 && parts.every(part => /^\d+$/.test(part))) {
            const [day, month, year] = parts.map(Number);
            const date = new Date(year, month - 1, day);
            // Проверяем корректность даты
            if (
                date.getFullYear() === year &&
                date.getMonth() === month - 1 &&
                date.getDate() === day
            ) {
                return date;
            }
        }
        return null;
    };

    const clearDate = () => {
        userInitiated.current = true;
        setInputValue('');
        setInternalDate(null);
        onChange(undefined);
    };

    const replaceCharWithPlaceholder = (str: string, index: number): string => {
        const paddedStr = str.padEnd(10, '_');
        return paddedStr.substring(0, index) + '_' + paddedStr.substring(index + 1);
    };

    const replaceRangeWithPlaceholder = (str: string, start: number, end: number): string => {
        const paddedStr = str.padEnd(10, '_');
        let result = paddedStr;
        for (let i = start; i < end && i < result.length; i++) {
            if (result[i] && result[i] !== '.') {
                result = result.substring(0, i) + '_' + result.substring(i + 1);
            }
        }
        return result;
    };

    const handleInputChange = (rawValue: string) => {
        setInputValue(rawValue);

        if (isEmptyMask(rawValue)) {
            userInitiated.current = true;
            setInternalDate(null);
            onChange(undefined);
            return;
        }

        const parsedDate = parseDate(rawValue);
        if (parsedDate) {
            userInitiated.current = true;
            setInternalDate(parsedDate);
            onChange(parsedDate);
        } else {
            userInitiated.current = true;
            setInternalDate(null);
        }
    };

    const handleBlur = () => {
        if (isEmptyMask(inputValue)) {
            clearDate();
            return;
        }

        const parsedDate = parseDate(inputValue);
        if (!parsedDate) {
            clearDate();
        }
    };

    const handleDateChange = (date: Date | null) => {
        if (date === null) {
            userInitiated.current = true;
            setInternalDate(null);
            setInputValue('');
            onChange(undefined);
        } else if (!internalDate || date.getTime() !== internalDate.getTime()) {
            userInitiated.current = true;
            setInternalDate(date);
            setInputValue(date.toLocaleDateString('ru-RU'));
            onChange(date);
        }
    };

    const saveSelection = () => {
        if (inputRef.current) {
            selectionRef.current = {
                start: inputRef.current.selectionStart,
                end: inputRef.current.selectionEnd
            };
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const input = inputRef.current;
        if (!input) {
            if (onKeyDown) onKeyDown(e);
            return;
        }

        let start = input.selectionStart ?? 0;
        let end = input.selectionEnd ?? 0;

        // Используем сохранённое выделение если текущее сброшено
        if (start === end && selectionRef.current.start !== null && selectionRef.current.end !== null
            && selectionRef.current.start !== selectionRef.current.end) {
            start = selectionRef.current.start;
            end = selectionRef.current.end;
        }

        const value = inputValue || '__.__.____';

        if (e.key === 'Delete') {
            e.preventDefault();
            if (start !== end) {
                const newValue = replaceRangeWithPlaceholder(value, start, end);
                handleInputChange(newValue);
                selectionRef.current = { start: null, end: null };
                setTimeout(() => input.setSelectionRange(start, start), 0);
            } else if (start < 10) {
                const paddedValue = value.padEnd(10, '_');
                const charAtPos = paddedValue[start];
                if (charAtPos === '.') {
                    setTimeout(() => input.setSelectionRange(start + 1, start + 1), 0);
                } else {
                    const newValue = replaceCharWithPlaceholder(paddedValue, start);
                    handleInputChange(newValue);
                    setTimeout(() => input.setSelectionRange(start + 1, start + 1), 0);
                }
            }
            return;
        }

        if (e.key === 'Backspace') {
            e.preventDefault();
            if (start !== end) {
                const newValue = replaceRangeWithPlaceholder(value, start, end);
                handleInputChange(newValue);
                selectionRef.current = { start: null, end: null };
                setTimeout(() => input.setSelectionRange(start, start), 0);
            } else if (start > 0) {
                const paddedValue = value.padEnd(10, '_');
                const charAtPos = paddedValue[start - 1];
                if (charAtPos === '.') {
                    setTimeout(() => input.setSelectionRange(start - 1, start - 1), 0);
                } else {
                    const newValue = replaceCharWithPlaceholder(paddedValue, start - 1);
                    handleInputChange(newValue);
                    setTimeout(() => input.setSelectionRange(start - 1, start - 1), 0);
                }
            }
            return;
        }

        if (onKeyDown) {
            onKeyDown(e);
        }
    };

    const handleMouseUp = () => {
        setTimeout(() => {
            if (inputRef.current && selectionRef.current.start !== null && selectionRef.current.end !== null) {
                const currentStart = inputRef.current.selectionStart;
                const currentEnd = inputRef.current.selectionEnd;
                if (currentStart === currentEnd && selectionRef.current.start !== selectionRef.current.end) {
                    inputRef.current.setSelectionRange(selectionRef.current.start, selectionRef.current.end);
                }
            }
        }, 0);
    };

    return (
        <DatePicker
            selected={internalDate}
            onChange={handleDateChange}
            customInput={
                <InputMask
                    inputRef={(el: HTMLInputElement) => {
                        (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
                    }}
                    mask="99.99.9999"
                    maskChar={inputValue ? "_" : null}
                    placeholder={placeholderText}
                    value={inputValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    onMouseDown={saveSelection}
                    onMouseUp={handleMouseUp}
                    onSelect={saveSelection}
                    disabled={disabled}
                    readOnly={readOnly}
                />
            }
            locale={locale}
            dateFormat={dateFormat}
            minDate={minDate}
            maxDate={maxDate}
            popperPlacement="bottom-start"
            popperContainer={({ children }) => (
                <div style={{ position: 'relative', zIndex: 10000 }}>
                    {children}
                </div>
            )}
            readOnly={readOnly}
            disabled={disabled}
            isClearable={!readOnly && !disabled}
        />
    );
};

export default MaskedDatePicker;